/**
 * Qué archivo se puede subir al bucket `photos`, dicho en el navegador ANTES de subirlo.
 *
 * Desde la 0314 el servidor tiene tope y lista de tipos (`storage.buckets.file_size_limit` y
 * `allowed_mime_types`), y esto es su espejo: lo que pasa acá, el servidor lo acepta. Si cambia
 * uno, cambia el otro en la misma tanda.
 *
 * **Por qué en el navegador, si el servidor ya rechaza.** Porque el rechazo del servidor llega
 * tarde y en inglés, y en los flujos con cola sin señal llega DESPUÉS de que la persona se fue de
 * la tienda: la marca o el canje quedan en la cola como «fallido» y ya no hay foto que volver a
 * tomar. Acá se rechaza en el momento de elegir, con un mensaje que dice qué hacer.
 *
 * **Qué se mide.** Lo que se va a SUBIR. Donde la foto se procesa al elegirla (el marcado la sella
 * y la recomprime), se mide el resultado; donde se sube tal cual o recién se comprime al enviar, se
 * mide el original, porque puede viajar tal cual si el navegador no logra recodificarlo (un HEIC
 * en Chrome, por ejemplo).
 *
 * **Por qué el tipo y no solo `image/*`.** Con `supabase-js` y un Blob, Storage registra el tipo
 * del Blob (`blob.type`), no la opción `contentType` de la subida. Un GIF o un archivo sin tipo
 * conocido (`''`, que viaja como `application/octet-stream`) que no se llegó a recodificar lo
 * rechazaría el servidor: mejor decirlo ahora.
 */

/** El mismo número que `file_size_limit` del bucket `photos` (0314). */
export const TOPE_SUBIDA_BYTES = 10 * 1024 * 1024;

/** Los tipos de foto que acepta el bucket. El PDF va aparte: solo lo aceptan los documentos. */
export const TIPOS_DE_FOTO: readonly string[] = [
  'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif',
];

export const TIPO_PDF = 'application/pdf';

export interface OpcionesArchivo {
  /** Deja pasar PDF además de fotos (solo la pantalla de Documentos). */
  pdf?: boolean;
}

function megas(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(1).replace('.', ',');
}

/** Motivo para rechazar el archivo por su TIPO, o `null` si el tipo se puede subir. */
export function rechazoPorTipo(f: Blob, opciones: OpcionesArchivo = {}): string | null {
  const tipo = (f.type || '').toLowerCase();
  if (TIPOS_DE_FOTO.includes(tipo)) return null;
  if (opciones.pdf === true && tipo === TIPO_PDF) return null;
  const permitidos = opciones.pdf === true
    ? 'una foto (JPG, PNG, WEBP o HEIC) o un PDF'
    : 'una foto JPG, PNG, WEBP o HEIC';
  if (tipo.startsWith('video/')) return `Eso es un video, no una foto. Elige ${permitidos}.`;
  return `Ese archivo no se puede subir. Elige ${permitidos}.`;
}

/** Motivo para rechazar el archivo por su TAMAÑO, o `null` si entra. */
export function rechazoPorTamano(f: Blob): string | null {
  if (f.size <= TOPE_SUBIDA_BYTES) return null;
  const que = (f.type || '').toLowerCase() === TIPO_PDF ? 'Ese archivo' : 'Esa foto';
  return `${que} pesa ${megas(f.size)} MB y el máximo es 10 MB. `
    + (que === 'Esa foto'
      ? 'Tómala de nuevo con menos resolución o elige otra.'
      : 'Escanéalo con menos resolución o elige otro.');
}

/** Tipo y tamaño juntos: el motivo del primero que falle, o `null` si el archivo se puede subir. */
export function rechazoDeArchivo(f: Blob, opciones: OpcionesArchivo = {}): string | null {
  return rechazoPorTipo(f, opciones) ?? rechazoPorTamano(f);
}

/**
 * Lo mismo para una selección de VARIAS fotos. Basta una que no sirva para rechazar la tanda, y
 * el mensaje la nombra: si se descartara en silencio, la persona creería haber subido cinco fotos
 * y habría subido cuatro.
 */
export function rechazoDeArchivos(
  archivos: readonly File[], opciones: OpcionesArchivo = {},
): string | null {
  for (const f of archivos) {
    const motivo = rechazoDeArchivo(f, opciones);
    if (motivo !== null) return archivos.length > 1 ? `«${f.name}»: ${motivo}` : motivo;
  }
  return null;
}
